import React, { ComponentProps, FC, ReactNode } from 'react';

import FaqSection from '@rover/components/src/FaqSection';
import HTMLBox from '@rover/kibble/patterns/HTMLBox';
import useTheme from '@rover/kibble/themes/useTheme';

export type LandingFaqItem = {
  question: string;
  answer: string;
  faqId?: number;
};

export type LandingFaqListProps = Omit<ComponentProps<typeof FaqSection>, 'faqItems'> & {
  faqs: LandingFaqItem[];
  children?: ReactNode;
};

const LandingFaqList: FC<LandingFaqListProps> = ({ faqs, children, ...sectionProps }) => {
  const { paragraph } = useTheme();
  const bodyCopy = paragraph['200'];

  // Answers arrive as HTML strings, so their tags are bare DOM nodes rather than
  // Kibble components and would inherit the smaller global body font. Give them
  // the styles a Paragraph would have, including its reset of the global ones.
  // The answer fills its accordion panel, so the outer spacing is dropped.
  const answerSx = {
    fontSize: bodyCopy.fontSize,
    fontWeight: bodyCopy.fontWeight,
    lineHeight: bodyCopy.lineHeight,
    '& p': { padding: 0, margin: 0, border: '0 none', ...bodyCopy },
    '& > :first-child': { marginTop: 0, paddingTop: 0 },
    '& > :last-child': { marginBottom: 0, paddingBottom: 0 },
  };

  if (faqs.length === 0) return null;

  return (
    <FaqSection
      {...sectionProps}
      faqItems={faqs.map(({ question, answer, faqId }) => ({
        faqId,
        itemHeading: question,
        itemBody: <HTMLBox text={answer} sx={answerSx} />,
      }))}
    >
      {children}
    </FaqSection>
  );
};

export default LandingFaqList;
